import { createPortal } from "react-dom";
import { X, FileText, Download } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import RichContent from "@/components/RichContent";
import { downloadFile, safeName } from "@/lib/download";

// Readable copy of exactly what was signed (the stored version snapshot), plus the signature block.
export default function SignedCopyModal({ sigId, onClose }) {
  const { data: d, isLoading } = useQuery({ queryKey: ["signed-copy", sigId], queryFn: () => api.get(`/me/agreements/${sigId}`).then((r) => r.data) });
  const pdf = async () => {
    try { await downloadFile(`/me/signatures/${sigId}/pdf`, `${safeName(d?.title)}.pdf`); }
    catch (e) { toast.error(formatApiError(e.message)); }
  };
  return createPortal(
    <div className="fixed inset-0 z-[210] bg-black/40 flex items-end sm:items-center justify-center sm:p-4" data-testid="signed-copy-modal">
      <div className="bg-[#FDFBF7] w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl p-5 sm:p-7">
        <div className="flex items-start justify-between gap-3 mb-3">
          <h3 className="font-serif text-2xl text-[#1A242B]">{d?.title || "Signed agreement"}</h3>
          <button onClick={onClose} aria-label="Close" data-testid="signed-copy-close" className="h-10 w-10 grid place-items-center rounded-full hover:bg-black/5"><X size={18} /></button>
        </div>
        {isLoading ? <p className="text-sm text-[#8FA89B]">Loading…</p> : d && (
          <>
            <p className="text-xs text-[#8FA89B] mb-4" data-testid="signed-copy-meta">Version {d.agreement_version} · signed {String(d.signed_at).slice(0, 16).replace("T", " ")} UTC</p>
            {d.pdf_path && <a href={`/api/files/${d.pdf_path}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-[#144F75] underline mb-3"><FileText size={14} /> {d.pdf_filename || "Agreement document (PDF)"}</a>}
            {d.content ? (
              <div className="rounded-xl border border-[#E5E0D8] bg-white p-4 text-sm text-[#4A5D6B] leading-relaxed" data-testid="signed-copy-content"><RichContent text={d.content} /></div>
            ) : !d.pdf_path && (
              <p className="text-sm text-[#4A5D6B]" data-testid="signed-copy-pdf-only">{d.pdf_stored ? "The exact text of this version is preserved in your signed PDF below." : "The exact text of this older version isn't on file. Your signature record (name, date and version) is kept."}</p>
            )}
            <div className="mt-4 rounded-xl bg-[#144F75]/[0.05] p-4 text-sm">
              <p className="text-[#1A242B]">Signed electronically by <b className="font-serif">{d.signature}</b>{d.name && d.name !== d.signature ? ` (${d.name})` : ""}</p>
            </div>
            <button onClick={pdf} data-testid="signed-copy-download" className="eyw-btn w-full justify-center mt-4"><Download size={15} /> Download signed PDF</button>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
